import { ContestType } from '@betterpool/api-types/contests-service';
import { createStyles, useMantineTheme } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';
import React, { useCallback, useMemo } from 'react';

import repeat from '~/common/utils/repeat';
import { SplashLogo } from '~/components/shared/Logos/SplashLogo';
import { useIsMobile } from '~/domains/common/hooks/mediaQueries';
import { Button } from '~/domains/design-system/Button';
import Dialog from '~/domains/design-system/components/Dialog/Dialog';
import DialogBody from '~/domains/design-system/components/Dialog/DialogBody/DialogBody';
import IconCashOff from '~/domains/design-system/icons/IconCashOff';
import { IconDeviceMobile } from '~/domains/design-system/icons/IconDeviceMobile';
import IconListNumbers from '~/domains/design-system/icons/IconListNumbers';
import IconMoneyBag from '~/domains/design-system/icons/IconMoneyBag';
import { Text } from '~/domains/design-system/Text';
import { Title } from '~/domains/design-system/Title';

const useStyles = createStyles((theme) => ({
  modal: {
    margin: theme.other.spacing._16,
  },
  wrapperContent: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.other.spacing._8,
    padding: theme.other.spacing._16,
    color: theme.colors.gray2[3],
  },
  body: {
    maxWidth: 480,
    textAlign: 'center',
  },
  title: {
    marginBottom: theme.other.spacing._8,
    padding: `0 ${theme.other.spacing._32}px`,
    textTransform: 'uppercase',
  },
  cta: {
    marginTop: theme.other.spacing._16,
    width: '100%',
  },
  featuresList: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: theme.other.spacing._32,
    marginBottom: theme.other.spacing._24,
    gap: theme.other.spacing._8,
  },
  feature: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.other.spacing._8,
    width: 'calc(100% / 3)',
  },
  featureIcons: {
    display: 'flex',
    padding: '0 calc(100% / 3 / 2 - 24px)',
    justifyContent: 'space-between',
  },
  featureIcon: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    backgroundColor: theme.colors.gray2[9],
    position: 'relative',
    color: theme.colors.blue[3],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  featureListItems: {
    display: 'flex',
  },
  featureText: {
    textAlign: 'center',
    color: theme.colors.gray2[4],
    padding: `0 ${theme.other.spacing._8}px`,
  },
  divider: {
    display: 'flex',
    gap: theme.other.spacing._8,
    marginTop: 24 - 3,
    height: 6,
  },
  dividerDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    backgroundColor: theme.colors.gray2[1],
  },
  bulletPointsWrapper: {
    backgroundColor: theme.colors.gray2[0],
    paddingTop: theme.other.spacing._8,
    paddingBottom: theme.other.spacing._8,
    paddingLeft: theme.other.spacing._16,
    paddingRight: theme.other.spacing._16,
    borderRadius: theme.radius.md,
    marginTop: theme.other.spacing._16,
    textAlign: 'left',
  },
  bulletPoint: {
    marginBottom: theme.other.spacing._8,
    marginLeft: theme.other.spacing._16,
  },
  bulletPointTitle: {
    fontWeight: 700,
  },
}));

function IntroModal({
  isOpen,
  onClose,
  isEntertainmentOnly = false,
  templateName,
  contestType,
  brand,
}: {
  isOpen: boolean;
  onClose: () => void;
  isEntertainmentOnly: boolean;
  templateName: string;
  contestType: 'player_one_and_done' | 'player_tier' | 'team_pick_em' | 'team_survivor';
  brand: 'RYP' | 'OFP';
}) {
  const theme = useMantineTheme();
  const { classes } = useStyles();
  const { t } = useTranslation('contest');

  const isMobile = useIsMobile();

  const goBack = useCallback(() => {
    window.history.back();
  }, []);

  const getFeatures = useCallback(() => {
    if (isEntertainmentOnly) {
      return [
        {
          icon: IconCashOff,
          text: t(`manage.common.modals.introModal.${contestType}.features.entertainmentOnly._1`, {
            templateName,
          }),
        },
        {
          icon: IconListNumbers,
          text: t(`manage.common.modals.introModal.${contestType}.features.entertainmentOnly._2`, {
            templateName,
          }),
        },
        {
          icon: IconDeviceMobile,
          text: t(`manage.common.modals.introModal.${contestType}.features.entertainmentOnly._3`, {
            templateName,
          }),
        },
      ];
    }

    return [
      {
        icon: IconMoneyBag,
        text: t(`manage.common.modals.introModal.${contestType}.features.paid._1`, {
          templateName,
        }),
      },
      {
        icon: IconListNumbers,
        text: t(`manage.common.modals.introModal.${contestType}.features.paid._2`, {
          templateName,
        }),
      },
      {
        icon: IconDeviceMobile,
        text: t(`manage.common.modals.introModal.${contestType}.features.paid._3`, {
          templateName,
        }),
      },
    ];
  }, [isEntertainmentOnly, t, templateName, contestType]);
  const features = useMemo(() => getFeatures(), [getFeatures]);

  const getBulletPoints = useCallback(() => {
    if (
      (contestType === ContestType.PLAYER_ONE_AND_DONE ||
        contestType === ContestType.PLAYER_TIER) &&
      brand
    ) {
      const keys = ['1', '2', '3'] as const;
      return keys.map((key) => ({
        title: t(
          `manage.common.modals.introModal.${contestType}.bulletPoints.${brand}._${key}.title`
        ),
        description: t(
          `manage.common.modals.introModal.${contestType}.bulletPoints.${brand}._${key}.description`
        ),
      }));
    }

    return [];
  }, [contestType, brand, t]);

  const bulletPoints = useMemo(() => getBulletPoints(), [getBulletPoints]);

  const featureDivider = (
    <div className={classes.divider}>
      {repeat(isMobile ? 3 : 6, (i) => (
        <div key={i} className={classes.dividerDot} />
      ))}
    </div>
  );

  return (
    <Dialog
      type="modal"
      isOpen={isOpen}
      onClose={onClose}
      className={classes.modal}
      wrapperContent={
        <div className={classes.wrapperContent}>
          <Text
            variant={
              isMobile ? 'display-headline-expanded-small' : 'display-headline-expanded-medium'
            }
          >
            {t('manage.common.modals.introModal.wrapperTitle')}
          </Text>
          <SplashLogo size={isMobile ? 18 : 20} color="#1a1a1a" />
        </div>
      }
    >
      <DialogBody className={classes.body}>
        <Title variant="display-headline-expanded-xl" className={classes.title}>
          {t(`manage.common.modals.introModal.${contestType}.title`, { templateName })}
        </Title>
        <Text variant="body-large">
          {t(`manage.common.modals.introModal.${contestType}.text`, { templateName })}
        </Text>

        <div className={classes.featuresList}>
          <div className={classes.featureIcons}>
            {features.map((feature, index) => (
              <React.Fragment key={feature.text}>
                <div className={classes.featureIcon}>
                  <feature.icon />
                </div>
                {index < features.length - 1 && featureDivider}
              </React.Fragment>
            ))}
          </div>
          <div className={classes.featureListItems}>
            {features.map((feature) => (
              <div className={classes.feature} key={feature.text}>
                <Text variant="body-medium" className={classes.featureText}>
                  {feature.text}
                </Text>
              </div>
            ))}
          </div>
        </div>

        {bulletPoints.length > 0 &&
          (contestType === ContestType.PLAYER_ONE_AND_DONE ||
            contestType === ContestType.PLAYER_TIER) && (
            <>
              <Text variant="body-large" color={theme.colors.gray2[3]}>
                {t(`manage.common.modals.introModal.${contestType}.bulletPointsTitle`)}
              </Text>
              <ul className={classes.bulletPointsWrapper}>
                {bulletPoints.map((bulletPoint) => (
                  <li key={bulletPoint.title} className={classes.bulletPoint}>
                    <Text variant="body-medium" className={classes.bulletPointTitle}>
                      {bulletPoint.title}
                    </Text>
                    <Text variant="body-medium">{bulletPoint.description}</Text>
                  </li>
                ))}
              </ul>
            </>
          )}

        <Button variant="primary-fill" onClick={onClose} className={classes.cta}>
          {t(`manage.common.modals.introModal.${contestType}.cta`)}
        </Button>

        {(contestType === ContestType.PLAYER_ONE_AND_DONE ||
          contestType === ContestType.PLAYER_TIER) && (
          <Button variant="text" onClick={goBack} className={classes.cta}>
            {t(`manage.common.modals.introModal.${contestType}.back`)}
          </Button>
        )}
      </DialogBody>
    </Dialog>
  );
}

export default IntroModal;
